import { defineModule } from '@/js/utils/helpers';

interface CalcI18n {
  totalPerMonth: string;
  totalPerMonthYearly: string;
  branchSingular: string;
  branchPlural: string;
  discountWord: string;
  yearTotal: string;
}

interface CalcConfig {
  basePrice: number;
  discount: number;
  tierMax: number;
  i18n: CalcI18n;
}

/**
 * Progressive enhancement for the pricing calculator. The markup is fully
 * server-rendered (vest = 1, monthly); this only updates text content and
 * toggles a `hidden` class, so no HTML is ever built from data.
 */
const setupCalculator = (root: HTMLElement): (() => void) | void => {
  const configEl = root.querySelector<HTMLScriptElement>('[data-calc-config]');
  if (!configEl?.textContent) return undefined;

  let config: CalcConfig;
  try {
    config = JSON.parse(configEl.textContent) as CalcConfig;
  } catch {
    return undefined;
  }

  const basePrice = Math.max(0, Number(config.basePrice) || 0);
  const discountPct = Math.min(100, Math.max(0, Number(config.discount) || 0));
  const tierMax = Math.max(1, Number(config.tierMax) || 1);
  const { i18n } = config;

  const nf = (decimals: number) =>
    new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  const nf0 = nf(0);
  const nf2 = nf(2);
  const euro = (value: number, decimals: number): string =>
    (decimals === 0 ? nf0 : nf2).format(value);

  const query = <T extends Element = HTMLElement>(selector: string) =>
    root.querySelector<T>(selector);

  const countEl = query('[data-calc-count]');
  const amountEl = query('[data-calc-amount]');
  const subEl = query('[data-calc-sub]');
  const totalEl = query('[data-calc-total]');
  const eyebrowEl = query('[data-calc-eyebrow]');
  const hintEl = query('[data-calc-hint]');
  const fineEl = query('[data-calc-fine]');
  const stdForm = query('[data-calc-standard]');
  const customForm = query('[data-calc-custom]');
  const stdPrice = query('[data-calc-standard-price]');
  const customPrice = query('[data-calc-custom-price]');
  const ctaStd = query('[data-calc-cta-standard]');
  const ctaCustom = query('[data-calc-cta-custom]');
  const stepBtns = Array.from(
    root.querySelectorAll<HTMLElement>('[data-calc-step]'),
  );
  const periodBtns = Array.from(
    root.querySelectorAll<HTMLElement>('[data-calc-period]'),
  );

  const state = { vest: 1, yearly: false };

  const toggle = (el: Element | null, visible: boolean) => {
    el?.classList.toggle('hidden', !visible);
  };

  const render = () => {
    const isCustom = state.vest > tierMax;

    if (countEl)
      countEl.textContent = isCustom ? `${tierMax}+` : String(state.vest);

    toggle(stdForm, !isCustom);
    toggle(customForm, isCustom);
    toggle(stdPrice, !isCustom);
    toggle(customPrice, isCustom);
    toggle(ctaStd, !isCustom);
    toggle(ctaCustom, isCustom);

    if (fineEl) {
      const fine = isCustom
        ? fineEl.getAttribute('data-fine-custom')
        : fineEl.getAttribute('data-fine-standard');
      if (fine !== null) fineEl.textContent = fine;
    }

    if (isCustom) return;

    const gross = basePrice * state.vest;
    const net = gross * (1 - (state.yearly ? discountPct / 100 : 0));

    if (amountEl) amountEl.textContent = euro(net, state.yearly ? 2 : 0);
    if (eyebrowEl) {
      eyebrowEl.textContent = state.yearly
        ? i18n.totalPerMonthYearly
        : i18n.totalPerMonth;
    }
    if (subEl) {
      const branch = state.vest === 1 ? i18n.branchSingular : i18n.branchPlural;
      let sub = `${euro(basePrice, 0)} × ${state.vest} ${branch}`;
      if (state.yearly)
        sub += ` · ${euro(gross - net, 2)} ${i18n.discountWord}`;
      subEl.textContent = sub;
    }
    if (totalEl) {
      toggle(totalEl, state.yearly);
      totalEl.textContent = state.yearly
        ? `${i18n.yearTotal}: ${euro(net * 12, 2)}`
        : '';
    }
  };

  const onStep = (event: Event) => {
    const delta =
      Number(
        (event.currentTarget as HTMLElement).getAttribute('data-calc-step'),
      ) || 0;
    state.vest = Math.min(tierMax + 1, Math.max(1, state.vest + delta));
    render();
  };

  const onPeriod = (event: Event) => {
    const btn = event.currentTarget as HTMLElement;
    state.yearly = btn.getAttribute('data-calc-period') === 'year';
    periodBtns.forEach((other) =>
      other.setAttribute('aria-pressed', other === btn ? 'true' : 'false'),
    );
    if (hintEl) {
      const next = state.yearly
        ? hintEl.getAttribute('data-hint-year')
        : hintEl.getAttribute('data-hint-month');
      if (next !== null) hintEl.textContent = next;
    }
    render();
  };

  stepBtns.forEach((btn) => btn.addEventListener('click', onStep));
  periodBtns.forEach((btn) => btn.addEventListener('click', onPeriod));

  render();

  return () => {
    stepBtns.forEach((btn) => btn.removeEventListener('click', onStep));
    periodBtns.forEach((btn) => btn.removeEventListener('click', onPeriod));
  };
};

let cleanups: Array<() => void> = [];

export default defineModule(
  () => {
    cleanups = Array.from(document.querySelectorAll<HTMLElement>('[data-calc]'))
      .map(setupCalculator)
      .filter((fn): fn is () => void => typeof fn === 'function');
  },
  () => {
    cleanups.forEach((fn) => fn());
    cleanups = [];
  },
);
